import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "../layout/index";
import { toast } from "react-toastify";

const PAGE_SIZE = 6;
const FEE_MIN = 0;
const FEE_MAX = 2000;

const AVATAR_COLORS = [
  "#004276", "#2E7D5E", "#0077B6", "#00897B",
  "#1565C0", "#00695C", "#0288D1", "#2E7D32",
];

function getInitials(firstName, lastName) {
  const f = (firstName || "?")[0] || "";
  const l = (lastName || "")[0] || "";
  return (f + l).toUpperCase() || "DR";
}

function getFullName(doc) {
  return (
    [doc.firstName, doc.middleName, doc.lastName].filter(Boolean).join(" ").trim() ||
    doc.fullName ||
    "Doctor"
  );
}

function getSpecialization(doc) {
  const val = doc.specialization || doc.speciality;
  if (!val) return "-";
  return Array.isArray(val) ? val.join(", ") : String(val);
}

function getQualification(doc) {
  const val = doc.qualification || doc.qualifications;
  if (!val) return null;
  return Array.isArray(val) ? val.join(", ") : String(val);
}

// ── Dual-range fee slider ─────────────────────────────────────────────────────
function FeeRangeSlider({ minVal, maxVal, onChange }) {
  const minPct = ((minVal - FEE_MIN) / (FEE_MAX - FEE_MIN)) * 100;
  const maxPct = ((maxVal - FEE_MIN) / (FEE_MAX - FEE_MIN)) * 100;

  return (
    <>
      <style>{`
        .fee-range { -webkit-appearance:none; appearance:none; position:absolute; width:100%; height:0; background:transparent; pointer-events:none; outline:none; }
        .fee-range::-webkit-slider-thumb { -webkit-appearance:none; width:16px; height:16px; border-radius:50%; background:#004172; border:2px solid #fff; box-shadow:0 1px 4px rgba(0,66,114,.35); cursor:pointer; pointer-events:all; }
        .fee-range::-moz-range-thumb { width:16px; height:16px; border-radius:50%; background:#004172; border:2px solid #fff; box-shadow:0 1px 4px rgba(0,66,114,.35); cursor:pointer; pointer-events:all; }
      `}</style>
      <div className="relative h-5 flex items-center mt-3">
        <div className="absolute w-full h-[3px] bg-gray-200 rounded-full" />
        <div
          className="absolute h-[3px] rounded-full"
          style={{ background: "#004172", left: `${minPct}%`, right: `${100 - maxPct}%` }}
        />
        <input
          type="range" className="fee-range" min={FEE_MIN} max={FEE_MAX} step={50} value={minVal}
          style={{ zIndex: minVal >= maxVal - 50 ? 5 : 3 }}
          onChange={(e) => onChange([Math.min(+e.target.value, maxVal - 100), maxVal])}
        />
        <input
          type="range" className="fee-range" min={FEE_MIN} max={FEE_MAX} step={50} value={maxVal}
          style={{ zIndex: 4 }}
          onChange={(e) => onChange([minVal, Math.max(+e.target.value, minVal + 100)])}
        />
      </div>
      <div className="flex justify-between mt-2">
        <span className="text-[11px] font-semibold text-[#004172]">₹{minVal}</span>
        <span className="text-[11px] font-semibold text-[#004172]">₹{maxVal}</span>
      </div>
    </>
  );
}

// ── Sidebar filter section title ───────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2.5">{children}</p>
  );
}

// ── Styled checkbox row ────────────────────────────────────────────────────────
function CheckRow({ label, sublabel, checked, onChange }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer group py-1">
      <div
        className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-all"
        style={{
          background: checked ? "#004172" : "white",
          borderColor: checked ? "#004172" : "#d1d5db",
        }}
      >
        {checked && (
          <svg width="9" height="9" viewBox="0 0 10 8" fill="none">
            <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <span className="text-xs text-gray-700 leading-tight">
        {label}
        {sublabel && <span className="text-gray-400 ml-1">{sublabel}</span>}
      </span>
      <input type="checkbox" className="hidden" checked={checked} onChange={onChange} />
    </label>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function CurebayReferralDoctors() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientcbcode, patient, specialty: initialSpecialty } = location.state || {};

  const DOCTOR_API_URL = process.env.REACT_APP_NEW_DOCTOR_URL;
  const PATIENTS_API_URL = process.env.REACT_APP_API_URL_PATIENTS;
  const IMG_BASE = process.env.REACT_APP_API_GOOGLE_URL || "";

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const [specialtyFilter, setSpecialtyFilter] = useState(initialSpecialty || "");
  const [symptomsFilter, setSymptomsFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [preferredDate, setPreferredDate] = useState(todayStr);

  // Sidebar filters
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [feeRange, setFeeRange] = useState([FEE_MIN, FEE_MAX]);
  const [selectedGenders, setSelectedGenders] = useState([]);

  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [symptomsList, setSymptomsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [activeFilters, setActiveFilters] = useState({ search: "", specialty: initialSpecialty, symptoms: "", date: todayStr });
  const [totalGenderCounts, setTotalGenderCounts] = useState({ M: 0, F: 0 });

  useEffect(() => {
    fetchSpecialties();
    fetchSymptoms();
    fetchDoctors(1, { search: "", specialty: initialSpecialty, symptoms: "", date: todayStr }, [FEE_MIN, FEE_MAX], [], []);
    fetchTotalGenderCounts();
  }, []);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    const filters = { search: nameFilter, specialty: specialtyFilter, symptoms: symptomsFilter, date: preferredDate };
    setActiveFilters(filters);
    setCurrentPage(1);
    fetchDoctors(1, filters, feeRange, selectedSlots, selectedGenders);
  }, [specialtyFilter, symptomsFilter, preferredDate]);

  // Debounced name search
  useEffect(() => {
    if (isFirstRender.current) return;
    const timer = setTimeout(() => {
      const filters = { search: nameFilter, specialty: specialtyFilter, symptoms: symptomsFilter, date: preferredDate };
      setActiveFilters(filters);
      setCurrentPage(1);
      fetchDoctors(1, filters, feeRange, selectedSlots, selectedGenders);
    }, 500);
    return () => clearTimeout(timer);
  }, [nameFilter]);

  // Re-fetch when fee range changes (debounced)
  useEffect(() => {
    if (isFirstRender.current) return;
    const timer = setTimeout(() => {
      const filters = { search: nameFilter, specialty: specialtyFilter, symptoms: symptomsFilter, date: preferredDate };
      fetchDoctors(1, filters, feeRange, selectedSlots, selectedGenders);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [feeRange]);

  // Re-fetch when slot or gender changes
  useEffect(() => {
    if (isFirstRender.current) return;
    const filters = { search: nameFilter, specialty: specialtyFilter, symptoms: symptomsFilter, date: preferredDate };
    fetchDoctors(1, filters, feeRange, selectedSlots, selectedGenders);
    setCurrentPage(1);
  }, [selectedSlots, selectedGenders]);

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith("http")) return photo;
    return `${IMG_BASE}${photo}`;
  };

  const fetchSpecialties = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const base = PATIENTS_API_URL?.endsWith("/") ? PATIENTS_API_URL : `${PATIENTS_API_URL}/`;
      const res = await fetch(`${base}Speciality/list?status=1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSpecialties(Array.isArray(data) ? data : data?.data || []);
    } catch { /* fail silently */ }
  };

  const fetchSymptoms = async () => {
    try {
      const res = await fetch(`${DOCTOR_API_URL}/Symptoms/list`);
      const data = await res.json();
      setSymptomsList(Array.isArray(data) ? data : data?.data || []);
    } catch { /* fail silently */ }
  };

  const fetchTotalGenderCounts = async () => {
    try {
      const base = `${DOCTOR_API_URL}/doctors/search/list`;
      const commonParams = `date=${todayStr}&consultationType=V&pageNumber=0&pageSize=1`;
      const [mRes, fRes] = await Promise.all([
        fetch(`${base}?${commonParams}&gender=M`),
        fetch(`${base}?${commonParams}&gender=F`),
      ]);
      const [mData, fData] = await Promise.all([mRes.json(), fRes.json()]);
      setTotalGenderCounts({
        M: mData?.total ?? 0,
        F: fData?.total ?? 0,
      });
    } catch { /* fail silently */ }
  };

  const fetchDoctors = async (page, filters, fee, slots = [], genders = []) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("date", filters.date || todayStr);
      params.set("consultationType", "V");
      params.set("pageNumber", String(page - 1));
      params.set("pageSize", String(PAGE_SIZE));
      params.set("feeMin", String(fee[0]));
      params.set("feeMax", String(fee[1]));
      if (filters.specialty) params.set("speciality", filters.specialty);
      if (filters.symptoms) params.set("symptom", filters.symptoms);
      if (filters.search) params.set("doctorName", filters.search);
      // Slot filter — comma-separated timeBuckets
      if (slots.length > 0) params.set("timeBuckets", slots.map((s) => s.toUpperCase()).join(","));
      // Gender filter — only pass when exactly one gender is selected
      if (genders.length === 1) params.set("gender", genders[0]);

      const res = await fetch(`${DOCTOR_API_URL}/doctors/search/list?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const items = Array.isArray(data) ? data : data?.doctors || [];
      setDoctors(items);
      setTotalElements(data?.total ?? items.length);
    } catch {
      toast.error("Failed to load doctors");
      setDoctors([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSpecialtyFilter("");
    setSymptomsFilter("");
    setNameFilter("");
    setPreferredDate(todayStr);
    setSelectedSlots([]);
    setFeeRange([FEE_MIN, FEE_MAX]);
    setSelectedGenders([]);
    const filters = { search: "", specialty: "", symptoms: "", date: todayStr };
    setActiveFilters(filters);
    setCurrentPage(1);
    fetchDoctors(1, filters, [FEE_MIN, FEE_MAX], [], []);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchDoctors(page, activeFilters, feeRange, selectedSlots, selectedGenders);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReferPatient = (doctor) => {
    navigate("/CurebayReferralBooking", {
      state: { patientcbcode, patient, referralDoctor: doctor },
    });
  };

  const toggleSlot = (slot) => {
    setSelectedSlots((prev) =>
      prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]
    );
  };

  const toggleGender = (g) => {
    setSelectedGenders((prev) =>
      prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
    );
  };

  const totalPages = Math.ceil(totalElements / PAGE_SIZE);

  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
    if (currentPage >= totalPages - 3)
      return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const hasSidebarFilters = selectedSlots.length > 0 || selectedGenders.length > 0 || feeRange[0] > FEE_MIN || feeRange[1] < FEE_MAX;


  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 md:pt-6 md:px-6 pb-10">

        {/* Page Header */}
        <div className="mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#004172] flex items-center justify-center">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="4" fill="white" opacity="0.9" />
                  <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="white" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0.9" />
                </svg>
              </div>
              <h1 className="text-base font-bold text-gray-900">CureBay Referral Doctors</h1>
            </div>
            <span className="inline-flex items-center gap-1.5 bg-[#E8F5F0] text-[#2E7D5E] text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[#C8E6C9]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#2E7D5E]" />
              CureBay Verified Network
            </span>
          </div>
          <p className="text-gray-400 text-xs mt-1.5 ml-9">
            Refer your patient to verified CureBay specialists across India
          </p>
          {patient && (
            <div className="inline-flex items-center gap-2 mt-2 ml-9 px-3 py-1.5 rounded-full"
              style={{ background: "linear-gradient(90deg, #e8f0fb 0%, #e6f7f0 100%)", border: "1px solid #c5dcf5" }}>
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #004172, #2E7D5E)" }}>
                {(patient.firstName || "P")[0].toUpperCase()}
              </div>
              <span className="text-sm font-bold text-[#004172]">
                {[patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(" ") || "Patient"}
              </span>
              {(patient.mobile || patient.mobileNumber || patient.phoneNumber || patient.phone) && (
                <>
                  <span className="w-1 h-1 rounded-full bg-[#004172]/30" />
                  <span className="text-xs font-semibold text-[#2E7D5E]">
                    {patient.mobile || patient.mobileNumber || patient.phoneNumber || patient.phone}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Top Search Bar */}
        <div className="bg-white border border-gray-200 rounded-xl p-3 mb-4 shadow-sm">
          <div className="flex flex-col lg:flex-row gap-2.5 items-end">
            {/* Doctor Name */}
            <div className="flex-1 min-w-0">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                  <path d="M20 20l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Search by Doctor Name"
                  className="w-full h-9 pl-8 pr-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-[#004172] focus:bg-white transition-colors"
                />
              </div>
            </div>

            {/* Symptoms */}
            <div className="flex-1 min-w-0">
              <select
                value={symptomsFilter}
                onChange={(e) => setSymptomsFilter(e.target.value)}
                className="w-full h-9 px-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700 outline-none focus:border-[#004172] focus:bg-white transition-colors"
              >
                <option value="">Select Symptoms</option>
                {symptomsList.map((s, i) => {
                  const name = s?.symptomsName || (typeof s === "string" ? s : "");
                  return name ? <option key={s.symptomsId || i} value={name}>{name}</option> : null;
                })}
              </select>
            </div>

            {/* Specialty */}
            <div className="flex-1 min-w-0">
              <select
                value={specialtyFilter}
                onChange={(e) => setSpecialtyFilter(e.target.value)}
                className="w-full h-9 px-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700 outline-none focus:border-[#004172] focus:bg-white transition-colors"
              >
                <option value="">Select Department</option>
                {specialties.map((s, i) => {
                  const name = s?.speciality || s?.trimSpeciality || (typeof s === "string" ? s : "");
                  return name ? <option key={s.id || i} value={s.speciality || name}>{name}</option> : null;
                })}
              </select>
            </div>

            {/* Date */}
            <div className="flex-shrink-0 w-40">
              <input
                type="date"
                value={preferredDate}
                onChange={(e) => setPreferredDate(e.target.value)}
                className="w-full h-9 px-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700 outline-none focus:border-[#004172] focus:bg-white transition-colors"
              />
            </div>

            {/* Reset */}
            <button
              onClick={handleReset}
              className="h-9 px-4 flex items-center gap-1.5 text-xs font-semibold text-[#004172] border border-[#004172]/30 bg-[#EEF4FA] rounded-lg hover:bg-[#004172] hover:text-white transition-all flex-shrink-0"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Reset
            </button>
          </div>
        </div>

        {/* Body: Sidebar + Content */}
        <div className="flex gap-4 items-start">

          {/* ── Sidebar ── */}
          <div className="w-52 flex-shrink-0 sticky top-4">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              {/* Sidebar header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M3 4h18M7 10h10M11 16h2" stroke="#004172" strokeWidth="2.2" strokeLinecap="round" />
                  </svg>
                  <span className="text-sm font-bold text-gray-800">Filters</span>
                </div>
                {hasSidebarFilters && (
                  <button
                    onClick={handleReset}
                    className="text-[10px] font-semibold text-[#004172] hover:underline"
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className="p-4 flex flex-col gap-5">

                {/* Select Slot */}
                <div>
                  <SectionTitle>Select Slot</SectionTitle>
                  <div className="flex flex-col gap-0.5">
                    <CheckRow
                      label="Morning"
                      sublabel="(9 - 12)"
                      checked={selectedSlots.includes("morning")}
                      onChange={() => toggleSlot("morning")}
                    />
                    <CheckRow
                      label="Afternoon"
                      sublabel="(12 - 5)"
                      checked={selectedSlots.includes("afternoon")}
                      onChange={() => toggleSlot("afternoon")}
                    />
                    <CheckRow
                      label="Evening"
                      sublabel="(5 - 9)"
                      checked={selectedSlots.includes("evening")}
                      onChange={() => toggleSlot("evening")}
                    />
                  </div>
                </div>

                <div className="border-t border-gray-100" />

                {/* Consultation Fee */}
                <div>
                  <SectionTitle>Consultation Fee</SectionTitle>
                  <FeeRangeSlider
                    minVal={feeRange[0]}
                    maxVal={feeRange[1]}
                    onChange={setFeeRange}
                  />
                </div>

                <div className="border-t border-gray-100" />

                {/* Preferred Doctor */}
                <div>
                  <SectionTitle>Preferred Doctor</SectionTitle>
                  <div className="flex flex-col gap-0.5">
                    <CheckRow
                      label="Male"
                      sublabel={totalGenderCounts.M > 0 ? `(${totalGenderCounts.M})` : ""}
                      checked={selectedGenders.includes("M")}
                      onChange={() => toggleGender("M")}
                    />
                    <CheckRow
                      label="Female"
                      sublabel={totalGenderCounts.F > 0 ? `(${totalGenderCounts.F})` : ""}
                      checked={selectedGenders.includes("F")}
                      onChange={() => toggleGender("F")}
                    />
                  </div>
                </div>

              </div>

              {/* Clear all footer */}
              {hasSidebarFilters && (
                <div className="px-4 pb-4">
                  <button
                    onClick={handleReset}
                    className="w-full py-2 text-xs font-semibold text-[#004172] border border-[#004172]/30 rounded-lg hover:bg-[#004172] hover:text-white transition-all"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Main Content ── */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-56 gap-3">
                <div className="w-8 h-8 border-2 border-[#004172] border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-gray-500">Loading doctors...</p>
              </div>
            ) : (
              <>
                {/* Available Doctors heading + count */}
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-bold text-gray-900">
                      Available Doctors{" "}
                      <span className="text-gray-400 font-normal">({doctors.length})</span>
                    </h2>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Select from trusted and verified healthcare professionals
                    </p>
                  </div>
                  {hasSidebarFilters && (
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      {selectedSlots.map((s) => (
                        <span key={s} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full capitalize">
                          {s}
                          <button onClick={() => toggleSlot(s)} className="hover:text-blue-900">×</button>
                        </span>
                      ))}
                      {selectedGenders.map((g) => (
                        <span key={g} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-100 px-2 py-0.5 rounded-full">
                          {g === "M" ? "Male" : "Female"}
                          <button onClick={() => toggleGender(g)} className="hover:text-purple-900">×</button>
                        </span>
                      ))}
                      {(feeRange[0] > FEE_MIN || feeRange[1] < FEE_MAX) && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-green-50 text-green-700 border border-green-100 px-2 py-0.5 rounded-full">
                          ₹{feeRange[0]}–₹{feeRange[1]}
                          <button onClick={() => setFeeRange([FEE_MIN, FEE_MAX])} className="hover:text-green-900">×</button>
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Empty state */}
                {doctors.length === 0 && (
                  <div className="flex flex-col items-center justify-center bg-white border border-gray-100 rounded-xl h-56 gap-3 text-gray-400">
                    <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-500">No doctors found</p>
                      <p className="text-xs mt-1">Try adjusting your filters or search term</p>
                    </div>
                  </div>
                )}

                {/* Doctor Cards Grid */}
                {doctors.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
                    {doctors.map((doctor, idx) => {
                      const fullName = getFullName(doctor);
                      const initials = getInitials(
                        doctor.firstName || doctor.first_name || doctor.name?.split(" ")[0],
                        doctor.lastName || doctor.last_name || doctor.name?.split(" ")[1]
                      );
                      const avatarColor = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                      const isOnLeave = doctor.onLeave === true || String(doctor.status || "").toUpperCase() === "ON_LEAVE";
                      const experience = doctor.yearsOfExperience != null ? `${doctor.yearsOfExperience} yrs` : "—";
                      const fee = doctor.effectiveConsultationFee != null
                        ? `₹${doctor.effectiveConsultationFee}`
                        : doctor.videoConsFee != null
                        ? `₹${doctor.videoConsFee}`
                        : "—";
                      const qualification = getQualification(doctor);
                      const photoUrl = getPhotoUrl(doctor.photo);

                      return (
                        <div
                          key={doctor.code || doctor.id || idx}
                          className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col shadow-sm hover:shadow-md transition-shadow relative"
                        >
                          {isOnLeave && (
                            <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-semibold bg-orange-50 text-orange-600 border border-orange-100 px-2 py-0.5 rounded-full z-10">
                              <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                              On Leave
                            </span>
                          )}

                          <div className="flex items-start gap-3 mb-3">
                            <div
                              className="w-12 h-12 rounded-full flex-shrink-0 relative overflow-hidden"
                              style={{ backgroundColor: avatarColor }}
                            >
                              <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm">{initials}</span>
                              {photoUrl && (
                                <img
                                  src={photoUrl}
                                  alt={fullName}
                                  className="absolute inset-0 w-full h-full object-cover rounded-full"
                                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                              )}
                            </div>

                            <div className="flex-1 min-w-0 pr-6">
                              <h3 className="font-bold text-gray-900 text-sm leading-tight">{fullName}</h3>
                              <p className="text-xs text-[#004172] font-semibold mt-0.5 line-clamp-2 leading-tight">
                                {getSpecialization(doctor)}
                              </p>
                              {qualification && (
                                <p className="text-[11px] text-gray-400 mt-0.5 leading-tight line-clamp-2">
                                  {qualification}
                                </p>
                              )}
                            </div>

                            <div className="absolute top-3 right-3 flex items-center gap-1.5">
                              {!isOnLeave && (
                                <>
                                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "#C8EAE2" }} title="Video Consultation">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                      <rect x="2" y="6" width="14" height="12" rx="2.5" stroke="#004172" strokeWidth="1.8" />
                                      <path d="M16 10.5l5.5-3v9l-5.5-3v-3z" stroke="#004172" strokeWidth="1.8" strokeLinejoin="round" />
                                    </svg>
                                  </div>
                                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "#C8EAE2" }} title="In-person Consultation">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                      <circle cx="12" cy="8" r="3.5" stroke="#004172" strokeWidth="1.8" />
                                      <path d="M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6" stroke="#004172" strokeWidth="1.8" strokeLinecap="round" />
                                    </svg>
                                  </div>
                                </>
                              )}
                              {isOnLeave && <div className="w-[68px]" />}
                            </div>
                          </div>

                          <div className="border-t border-gray-100 mb-3" />

                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Experience</p>
                              <p className="text-xs font-bold text-gray-800 mt-0.5">{experience}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Consultation fees</p>
                              <p className="text-xs font-bold text-gray-800 mt-0.5">{fee}</p>
                            </div>
                          </div>

                          <button
                            onClick={() => !isOnLeave && handleReferPatient(doctor)}
                            disabled={isOnLeave}
                            className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed mt-auto border"
                            style={{
                              background: isOnLeave ? "#f3f4f6" : "linear-gradient(90deg, #e8f4f0 0%, #ddeef8 100%)",
                              color: isOnLeave ? "#9ca3af" : "#004276",
                              borderColor: isOnLeave ? "#e5e7eb" : "#b8d9ec",
                            }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                              <path d="M5 12h14M13 6l6 6-6 6" stroke={isOnLeave ? "#9ca3af" : "#004276"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Refer Patient
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-gray-500">
                      Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalElements)} of {totalElements}
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="flex items-center gap-1 text-xs text-gray-600 border border-gray-200 bg-white px-3 py-1.5 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        Prev
                      </button>

                      {getPageNumbers().map((p, i) =>
                        p === "..." ? (
                          <span key={`ellipsis-${i}`} className="px-2 text-xs text-gray-400">…</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => handlePageChange(p)}
                            className={`min-w-[32px] h-8 text-xs rounded-lg border transition-colors ${
                              currentPage === p
                                ? "bg-[#004172] text-white border-[#004172]"
                                : "text-gray-600 border-gray-200 bg-white hover:bg-gray-50"
                            }`}
                          >
                            {p}
                          </button>
                        )
                      )}

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="flex items-center gap-1 text-xs text-gray-600 border border-gray-200 bg-white px-3 py-1.5 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors"
                      >
                        Next
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
